import { useState, useEffect, useCallback } from 'react';
import { TIME_OF_DAY_CONFIGS, getTimePeriod } from './timeOfDayConfig.js';
import type { TimePeriod, TimeOfDayConfig } from './timeOfDayConfig.js';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseTimeOfDayOptions {
  /** Override the period for dev/testing. When set, disables auto-detection. */
  overridePeriod?: TimePeriod | null;
}

interface UseTimeOfDayReturn {
  period: TimePeriod;
  config: TimeOfDayConfig;
}

/**
 * Hook that returns the current time-of-day period and its configuration.
 *
 * - Detects period on mount from device local time
 * - Rechecks every 5 minutes for real-time transitions
 * - Accepts optional override for dev mode testing
 */
export function useTimeOfDay(options: UseTimeOfDayOptions = {}): UseTimeOfDayReturn {
  const { overridePeriod = null } = options;

  const detectPeriod = useCallback((): TimePeriod => {
    const hour = new Date().getHours();
    const period = getTimePeriod(hour);
    console.log('[useTimeOfDay] Current hour:', hour, '→ Period:', period);
    return period;
  }, []);

  const [period, setPeriod] = useState<TimePeriod>(() => {
    return overridePeriod ?? detectPeriod();
  });

  // Update when override changes (or immediately detect real period when override is cleared)
  useEffect(() => {
    if (overridePeriod !== null) {
      setPeriod(overridePeriod);
    } else {
      setPeriod(detectPeriod());
    }
  }, [overridePeriod, detectPeriod]);

  // Auto-detect period changes via interval (only when no override)
  useEffect(() => {
    if (overridePeriod !== null) return;

    const interval = setInterval(() => {
      const newPeriod = detectPeriod();
      setPeriod((prev) => {
        if (prev !== newPeriod) return newPeriod;
        return prev;
      });
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [overridePeriod, detectPeriod]);

  const config = TIME_OF_DAY_CONFIGS[period];

  return { period, config };
}
